import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { UserAuth } from '../../authentication/AuthContext.jsx';
import { refreshUserDetails } from '../../authentication/refreshUserDetails.js';
import { RECONNECTED_EVENT } from '../app-status/appStatus.js';

// Tried again after these waits before the shell says it could not load.
export const BOOTSTRAP_RETRY_DELAYS_MS = [1000, 2000, 4000];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Loads the signed-in user's profile and effective permissions for a shell (DashboardLayout, the Developer Tools
 * shell) - the data every guard and the nav wait on. A failure used to be logged and forgotten, leaving the content
 * blank for good. Now it is tried again (1s, 2s, 4s); still failing, `failed` is true so the shell shows
 * "We couldn't load your workspace" with Try again, and it tries again by itself when the connection comes back or
 * the user returns to the tab.
 *
 * @param {{ loaded: boolean }} args  true when the data is already in the store (loaded at sign-in) - nothing to do
 * @returns {{ failed: boolean, retry: () => void }}
 */
export default function useWorkspaceBootstrap({ loaded }) {
    const dispatch = useDispatch();
    const { user } = UserAuth();
    const accessToken = user?.accessToken;
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);

    const retry = useCallback(() => setAttempt((n) => n + 1), []);

    useEffect(() => {
        if (!accessToken || loaded) return undefined;
        let cancelled = false;
        setFailed(false);
        (async () => {
            for (let i = 0; ; i += 1) {
                try {
                    await refreshUserDetails({ accessToken, dispatch });
                    return;
                } catch (error) {
                    if (cancelled) return;
                    console.error('Failed to load the workspace:', error);
                    if (i >= BOOTSTRAP_RETRY_DELAYS_MS.length) {
                        setFailed(true);
                        return;
                    }
                    await sleep(BOOTSTRAP_RETRY_DELAYS_MS[i]);
                    if (cancelled) return;
                }
            }
        })();
        return () => { cancelled = true; };
        // `loaded` is read when the load starts; it turning true is this load succeeding, not a reason to re-run.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, attempt, dispatch]);

    // Gave up: try again when the connection is back or the user comes back to the tab.
    useEffect(() => {
        if (!failed) return undefined;
        const onVisible = () => { if (document.visibilityState === 'visible') retry(); };
        window.addEventListener(RECONNECTED_EVENT, retry);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.removeEventListener(RECONNECTED_EVENT, retry);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [failed, retry]);

    return { failed, retry };
}
